import type { HelpSectionContent } from './types';

// CB:BEGIN pm-help-content
export const propertyManagerHelpContent: HelpSectionContent[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Set up your account and understand how CoreBids works for property managers.',
    tags: ['Setup', 'Onboarding', 'Account'],
    overview: 'CoreBids gives property managers a single platform to manage roofing and exterior projects across your entire portfolio — from bid collection to escrow-protected payments.',
    steps: [
      { title: 'Complete onboarding', body: 'Sign the platform agreement and fill in your company profile during the guided onboarding flow.' },
      { title: 'Add your properties', body: 'Add properties one at a time from the Properties panel on your dashboard.' },
      { title: 'Create your first project', body: 'Click "+ New Project", select a property, and submit a roofing project to start receiving contractor bids.' },
      { title: 'Review and select a contractor', body: 'Compare bids on the project detail page and select the contractor you want. The CoreBids team coordinates milestone-based payments from there.' },
    ],
    nextStep: 'Add your first property to get started.',
    notifications: [
      { title: 'Welcome email', body: 'Sent when your account is created and the platform agreement is signed.', type: 'info' },
      { title: 'Bid received', body: 'Notifies you when a contractor submits a bid on your project.', type: 'action' },
    ],
    mistakesToAvoid: [
      'Do not skip the platform agreement — projects cannot go live until it is signed.',
      'Projects start as drafts. Click "Sign & Activate" when ready to notify contractors.',
    ],
    faqs: [
      { question: 'How many properties can I add?', answer: 'There is no limit. Add as many as your portfolio requires.' },
      { question: 'Can I manage properties in multiple cities?', answer: 'Yes. CoreBids supports multi-city portfolios. Contractors are matched by their service area.' },
    ],
  },
  {
    id: 'adding-properties',
    title: 'Adding Properties',
    description: 'Add properties to your portfolio to create projects and track work history.',
    tags: ['Properties', 'Portfolio'],
    overview: 'The Properties panel lets you manage every address in your portfolio. Add properties individually — each becomes a home for one or more projects.',
    steps: [
      { title: 'Open the Properties panel', body: 'Click "Properties" in the navigation header to see your full property list.' },
      { title: 'Click "+ Add Property"', body: 'Opens the Add Property form. Enter the street address, city, state, and ZIP code.' },
      { title: 'Add a property nickname (optional)', body: 'A label like "Downtown Office Bldg A" helps identify it across your dashboard.' },
      { title: 'Add notes (optional)', body: 'Use the Notes field for internal information: access codes, tenant contacts, HOA rules, or insurance details.' },
    ],
    nextStep: 'Create a project under the property to request contractor bids.',
    notifications: [],
    mistakesToAvoid: [
      'Each property needs at minimum: address, city, state, zip.',
      'Properties can be edited after creation from the property detail page.',
    ],
    faqs: [
      { question: 'Can I edit a property after saving it?', answer: 'Yes. Open the property detail page and click the edit option to update the address, nickname, or notes.' },
      { question: 'Is bulk CSV import available?', answer: 'Yes — use the Import option to upload a CSV. Download the template from the Import dialog to see required column format.' },
    ],
  },
  {
    id: 'creating-projects',
    title: 'Creating & Activating Projects',
    description: 'Submit roofing projects for your properties and send them live to contractors.',
    tags: ['Projects', 'Draft', 'Activate'],
    overview: 'Every project starts as a draft. You review it, then sign to activate — at which point contractors in your area are notified and can submit bids.',
    steps: [
      { title: 'Click "+ New Project"', body: 'Opens the project form. Select an existing property or create one inline by clicking "Create new property".' },
      { title: 'Set dates', body: 'Bid Response Deadline defaults to 2 days out. Site Visit By defaults to 14 days. Adjust as needed.' },
      { title: 'Save as Draft', body: 'Click "Save Project Draft". The project is saved but contractors are not yet notified.' },
      { title: 'Sign & Activate', body: 'Open the draft project, click "Sign & Activate", type your full name to confirm, and the project goes live.' },
    ],
    nextStep: 'Contractors will respond within your bid deadline window.',
    notifications: [
      { title: 'Draft reminder', body: 'If a draft sits unsigned, the dashboard shows a needs-attention banner.', type: 'warning' },
      { title: 'Project live', body: 'Confirmation that contractors have been notified.', type: 'success' },
    ],
    mistakesToAvoid: [
      'Drafts are not visible to contractors until activated.',
      'Setting a very short bid deadline may reduce the number of bids you receive.',
    ],
    faqs: [
      { question: 'Can I edit a project after activating it?', answer: 'Contact support at support@corebids.com for any changes needed after a project is activated.' },
      { question: 'Why does the project start as a draft?', answer: 'It gives you a chance to review all details before contractors are notified — avoiding errors or premature outreach.' },
    ],
  },
  {
    id: 'reviewing-bids',
    title: 'Reviewing Bids',
    description: 'Compare contractor bids and select the best fit for your property.',
    tags: ['Bids', 'Contractors', 'Selection'],
    overview: 'Once bids arrive, your project status updates automatically. You can compare bids on the project detail page and select the contractor you want to move forward with.',
    steps: [
      { title: 'Open the project from your dashboard or Projects list', body: 'Active projects with bids show a "Bids Ready" or "Deciding" status. Click to open.' },
      { title: 'Review each proposal', body: "Each bid card shows the contractor's company name, submitted price, and any included scope notes." },
      { title: 'Select a contractor', body: 'Click "Select This Contractor" on the winning bid. The contractor is notified immediately and your project advances.' },
      { title: 'Escrow and payment are coordinated', body: 'After selection, the CoreBids team coordinates escrow milestone setup. You will receive email updates at each payment milestone.' },
    ],
    nextStep: 'Approve milestones as work is completed to release funds.',
    notifications: [
      { title: 'Bid received', body: 'Email notification each time a contractor submits a bid.', type: 'action' },
      { title: 'Contractor selected', body: 'Confirmation sent to both you and the selected contractor.', type: 'success' },
    ],
    mistakesToAvoid: [
      'Do not wait too long after bids arrive — contractors may become unavailable.',
      'Review the contractor name and price carefully before confirming selection.',
    ],
    faqs: [
      { question: 'How many bids will I get?', answer: 'It depends on contractor availability and your deadline. Most projects receive 2–5 bids.' },
      { question: 'Can I change my contractor after selecting?', answer: 'Contact support at support@corebids.com if you need to change the selected contractor before work begins.' },
    ],
  },
  {
    id: 'payments-milestones',
    title: 'Payments & Milestones',
    description: 'How escrow-protected milestone payments work for PM projects.',
    tags: ['Payments', 'Escrow', 'Milestones'],
    overview: 'CoreBids uses milestone-based escrow payments. Funds are held securely and released by the CoreBids team after each phase of work is verified — protecting both you and the contractor.',
    steps: [
      { title: 'Escrow is set up after contractor selection', body: 'The CoreBids team coordinates escrow milestone setup based on the project scope and contractor bid.' },
      { title: 'Funds go into escrow', body: 'Payment is collected and held securely by CoreBids until each milestone is verified.' },
      { title: 'CoreBids reviews milestone evidence', body: 'When the contractor marks a phase done and submits photos/documentation, the CoreBids team reviews the evidence before releasing funds.' },
      { title: 'Final release on completion', body: 'The last payment is released when all work is complete and verified.' },
    ],
    nextStep: 'Contact support@corebids.com if there is a concern about milestone completion or quality.',
    notifications: [
      { title: 'Milestone submitted', body: 'Notifies you when a contractor marks a phase complete. CoreBids reviews the evidence.', type: 'info' },
      { title: 'Payment released', body: 'Confirms that escrow funds have been released to the contractor after CoreBids approval.', type: 'success' },
    ],
    mistakesToAvoid: [
      'Do not wait to raise concerns — contact support before milestone funds are released if work does not meet your standards.',
      'Milestone payments are coordinated by the CoreBids team for PM projects, not by the PM directly.',
    ],
    faqs: [
      { question: 'What if I have a concern about work quality?', answer: 'Email support@corebids.com with your project ID and describe the issue. CoreBids can hold funds while the dispute is reviewed.' },
      { question: 'Are there platform fees?', answer: 'CoreBids charges a small platform fee (shown before any payment is confirmed) on top of the contractor price.' },
    ],
  },
  {
    id: 'documents',
    title: 'Managing Documents',
    description: 'Property-level and project-level documents across your portfolio.',
    tags: ['Documents', 'Files', 'Portfolio'],
    overview: 'The Documents page lets you view and download all files associated with your properties and projects — including insurance documents, permits, contracts, and milestone evidence.',
    steps: [
      { title: 'Open Documents from the navigation header', body: 'Click "Documents" in the top navigation bar to open the portfolio-wide document view.' },
      { title: 'Expand a property to see its documents', body: 'Click any property row to load its property-level documents and the documents for each associated project.' },
      { title: 'Download any file', body: 'Click the download icon next to a document to open a secure, time-limited download link.' },
      { title: 'Upload property documents', body: 'Use the upload option from the property detail page to add insurance certificates, permits, or HOA documents.' },
    ],
    nextStep: 'Keep insurance and permit documents up to date for each active project.',
    notifications: [],
    mistakesToAvoid: [
      'Property documents (insurance, permits) are stored at the property level — not per project.',
      'Project documents (contracts, milestone evidence) are linked to individual projects.',
    ],
    faqs: [
      { question: 'Where are signed contracts stored?', answer: 'Signed contracts appear under the project\'s document section once the contractor signing is complete.' },
      { question: 'Can I delete a document?', answer: 'Property-level documents can be deleted from the Documents page. Project documents are managed by the CoreBids team.' },
    ],
  },
];
// CB:END pm-help-content
